import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, DownloadSimple, MagicWand, CircleNotch } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, assetUrl, errDetail } from "../lib/api";

export const ImagePreview = ({ url, name, onClose, sourceType, sourceId }) => {
  const [activeUrl, setActiveUrl] = useState(url);
  const [activeName, setActiveName] = useState(name);
  const [activeSource, setActiveSource] = useState(sourceType && sourceId ? { type: sourceType, id: sourceId } : null);
  const [upscaling, setUpscaling] = useState(false);
  const downloadUrl = `${activeUrl}${activeUrl.includes("?") ? "&" : "?"}download=1`;

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  const upscale = async () => {
    if (!activeSource) return;
    setUpscaling(true);
    try {
      const { data } = await api.post("/upscale",
        { source_type: activeSource.type, source_id: activeSource.id, scale: 2 },
        { timeout: 240000 });
      setActiveUrl(assetUrl(data.asset_id));
      setActiveName(`triad-upscaled-${data.asset_id.slice(0, 6)}`);
      setActiveSource({ type: "asset", id: data.asset_id });
      toast.success("Upscaled — click download for full-res");
    } catch (e) {
      toast.error(errDetail(e));
    } finally {
      setUpscaling(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center scale-in"
      data-testid="image-preview-modal" onClick={onClose}>
      <div className="absolute top-4 right-4 flex items-center gap-2 z-10">
        {activeSource && (
          <button data-testid="image-preview-upscale" onClick={(e) => { e.stopPropagation(); upscale(); }}
            disabled={upscaling} title="Upscale (pixel-perfect Real-ESRGAN)"
            className="flex items-center gap-1.5 text-white/90 hover:text-white bg-white/10 hover:bg-white/20 px-3 py-2 rounded-full text-xs font-semibold transition-colors duration-200 disabled:opacity-50">
            {upscaling ? <CircleNotch size={14} className="animate-spin" /> : <MagicWand size={14} weight="fill" />}
            {upscaling ? "Upscaling…" : "Upscale"}
          </button>
        )}
        <a href={downloadUrl} download={activeName} data-testid="image-preview-download" title="Download" onClick={(e) => e.stopPropagation()}
          className="text-white/80 hover:text-white bg-white/10 hover:bg-white/20 p-2.5 rounded-full transition-colors duration-200">
          <DownloadSimple size={20} />
        </a>
        <button onClick={onClose} data-testid="image-preview-close" title="Close (Esc)"
          className="text-white/80 hover:text-white bg-white/10 hover:bg-white/20 p-2.5 rounded-full transition-colors duration-200">
          <X size={20} />
        </button>
      </div>
      <img src={activeUrl} alt={activeName} onClick={(e) => e.stopPropagation()}
        className="max-h-[88vh] max-w-[92vw] object-contain rounded-lg shadow-2xl select-none" />
    </div>,
    document.body
  );
};
